import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import {
  Grid,
  InputAdornment,
  TextField,
  Paper,
  CircularProgress,
  Button,
  Hidden,
} from '@material-ui/core';
import debounce from 'lodash.debounce';
import { parse, stringify } from 'query-string';
import { Helmet } from 'react-helmet';
import clsx from 'clsx';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import {
  ROUTE_DISCOVER_GENRE,
  ROUTE_DISCOVER_SEARCH,
  ROUTE_PARTNER_DISCOVER_SEARCH,
} from '@/constants/routes';
import appsListStatuses from '@/constants/app';
import { LOCATIONS_DISCOVER_PAGE, LOCATIONS_ON_CARD } from '@/constants/locations';

// UTILITY
import { sendParentMessage, getSearchParams } from '@/utility/common';
import { parseStingToArray, routeEnhance, pagePartnerProtector, getLink } from '@/utility/routes';
import {
  analyticsPageLoaded,
  analyticsUpdateAppListStatus,
  analyticsLeftSearch,
  analyticsSearched,
  customAnalyticsEvent,
} from '@/utility/analytics';
import { getOrgSchema } from '@/utility/orgSchemas/orgSchemas';
import { getS3ImageUrl } from '@/utility/content';

// STORE
import {
  WatchListActions,
  OnboardingActions,
  DiscoverActions,
  AppDetailsActions,
} from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import ContentHero from '@/components/Heroes/ContentHero';
import AppCardSkeleton from '@/components/Skeletons/AppCardSkeleton';
import AppCard from '@/components/Cards/AppCardWithTooltip';

// HELP
import Filters from './help/Filters';
import NoResults from './help/NoResults';

// STYLES
import { Wrapper } from './styles';

const defaultOGImg = getS3ImageUrl('/og-image/apps.png');

const types = [
  { text: 'common.free', value: 'free' },
  { text: 'common.paid', value: 'paid' },
];

const filterInitialParams = {
  search: '',
  genres: [],
  channels: [],
  keywords: [],
  type: ['paid', 'free'],
  devices: '',
  show_id: '',
  movie_id: '',
};

class StreamingMarketplaceDiscover extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      openFilters: false,
      params: {
        ...filterInitialParams,
        page: props.current_page,
        per_page: props.per_page,
      },
    };
    this.debouncedSetQueryParams = debounce(data => {
      this.setQueryParams({ ...data, page: 1 });
    }, 1000);
  }

  componentDidMount() {
    const { isIframe, getDiscoverFilters, partner } = this.props;

    pagePartnerProtector(partner, ['use_marketplace']);
    getDiscoverFilters();
    this.checkMethod();

    if (isIframe) {
      sendParentMessage({ goToTop: true });
    }
  }

  componentDidUpdate(prevProps) {
    const {
      router: { navigate, location, routerMatch },
      isAuth,
      partner,
      loading,
      list,
    } = this.props;
    const isPrevPathDiscoverGenre = prevProps.router.routerMatch({ path: ROUTE_DISCOVER_GENRE });
    const isDiscoverGenre = routerMatch({ path: ROUTE_DISCOVER_GENRE });

    pagePartnerProtector(partner, ['use_marketplace']);

    const params = this.getSearchParams(location.search);
    const prevParams = this.getSearchParams(prevProps.router.location.search);
    const search = params.toString();
    const prevSearch = prevParams.toString();

    if (isDiscoverGenre) {
      if (prevProps.router.location.key !== location.key) {
        this.checkMethod();
      }

      return;
    }

    if (isPrevPathDiscoverGenre || prevSearch !== search) {
      if (prevSearch && !search) {
        navigate(-1);
        return;
      }

      this.checkMethod();
    }

    if (prevProps.isAuth !== isAuth) {
      this.checkMethod();
    }

    if (prevProps.loading !== loading && !loading && list.length) {
      const properties = {
        query: params.get('search'),
        numberOfItemsDisplayed: list.length,
        first_item_type: list[0].type,
        first_item_name: list[0].title,
        first_item_id: list[0].id,
        search_location: LOCATIONS_DISCOVER_PAGE,
      };

      analyticsSearched(properties);
    }
  }

  componentWillUnmount() {
    const { cleanDiscoverList } = this.props;
    cleanDiscoverList();
  }

  getSearchParams = search => {
    const params = getSearchParams(search);
    params.delete('iframe');
    params.delete('partner');
    params.delete('page');
    params.delete('per_page');

    return params;
  };

  checkMethod = () => {
    const {
      router: { location, routerMatch },
    } = this.props;
    const isGenreRoute = routerMatch({ path: ROUTE_DISCOVER_GENRE });

    if (isGenreRoute) {
      this.parseSertainUrl();
      return;
    }

    const params = this.getSearchParams(location.search);
    const search = params.toString();

    if (search) {
      this.setParams();
    } else {
      this.setQueryParams();
    }
  };

  parseSertainUrl = () => {
    const { params } = this.state;
    const {
      router: {
        params: { slug },
        routerMatch,
      },
    } = this.props;
    const isGenreRoute = routerMatch({ path: ROUTE_DISCOVER_GENRE });

    const newParams = { ...filterInitialParams, page: 1, per_page: params.per_page };

    if (isGenreRoute) {
      newParams.genres = slug.split(',');
    }

    this.setState({ params: newParams });
    this.getList(newParams);
  };

  setParams = () => {
    const { params } = this.state;
    const queryParams = this.formatQueryParams();
    const newParams = {
      ...filterInitialParams,
      type: [],
      ...queryParams,
    };
    delete newParams.iframe;

    if (!newParams.page) {
      newParams.page = 1;
    }

    if (!newParams.per_page) {
      newParams.per_page = params.per_page;
    }

    this.setState({ params: newParams });
    this.getList(newParams);
  };

  formatQueryParams = () => {
    const {
      router: { location },
    } = this.props;

    const params = this.getSearchParams(location.search);
    const search = params.toString();

    const queryParams = parse(search, {
      skipEmptyString: true,
      parseNumbers: true,
    });

    if (!Array.isArray(queryParams.genres) && queryParams.genres) {
      queryParams.genres = parseStingToArray(queryParams.genres, ',');
    }
    if (!Array.isArray(queryParams.channels) && queryParams.channels) {
      queryParams.channels = parseStingToArray(queryParams.channels, ',', 'number');
    }
    if (!Array.isArray(queryParams.keywords) && queryParams.keywords) {
      queryParams.keywords = parseStingToArray(queryParams.keywords, ',', 'number');
    }
    if (!Array.isArray(queryParams.type) && queryParams.type) {
      queryParams.type = parseStingToArray(queryParams.type, ',');
    }

    return queryParams;
  };

  setQueryParams = (newParams = {}) => {
    const { params: stateParams } = this.state;
    const {
      router: { navigate },
      isIframe,
    } = this.props;

    const params = { ...stateParams, ...newParams };
    const qsParams = { ...params };
    delete qsParams.page;
    delete qsParams.per_page;

    const search = stringify(qsParams, {
      arrayFormat: 'comma',
      skipEmptyString: true,
    });

    const linkWithParams = routeEnhance(`${ROUTE_DISCOVER_SEARCH}?${search}`);

    if (window?.scrollTo) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }

    if (isIframe) {
      sendParentMessage({ goToTop: true });
    }

    this.setState({ params }, () => {
      navigate(linkWithParams);
    });
  };

  getList = queryParams => {
    const { params } = this.state;
    const { partnerSlug, partner, getDiscoverList } = this.props;

    getDiscoverList({
      ...params,
      ...queryParams,
      partner: partner.partner_slug || partnerSlug || '',
    });
  };

  handleChange = (name, value) => {
    const { params } = this.state;
    const data = {
      [name]: value,
      page: 1,
    };

    this.setState({ params: { ...params, ...data } }, this.debouncedSetQueryParams);
  };

  handleLoadMore = () => {
    const { params } = this.state;

    this.setState({ params: { ...params, page: params.page + 1 } }, this.getList);
  };

  handleReset = type => {
    const isFullSearch = type === 'full-search';

    customAnalyticsEvent('Streaming Store Action', {
      category: isFullSearch ? 'Full Search' : 'Clicked Reset',
    });

    this.setState({ openFilters: false }, () => {
      if (!isFullSearch) {
        this.setQueryParams({ ...filterInitialParams, page: 1 });
      }
    });
  };

  toggleFilters = () => {
    const { openFilters } = this.state;
    this.setState({ openFilters: !openFilters });
  };

  handleUpdateAppStatus = (data, status) => {
    const { appListUpdateAppStatuses, isAuth, partner, user } = this.props;
    const apps = [{ id: data.id, status }];

    if (status) {
      if (isAuth) {
        appListUpdateAppStatuses(apps);
      } else {
        this.checkMethod();
      }

      if (status === appsListStatuses.have) {
        analyticsUpdateAppListStatus('Added App To Profile', {
          app_id: data.id,
          app_name: data.name,
          user_id: user?.id || '',
          location: LOCATIONS_ON_CARD,
          partner_id: partner.id || 0,
        });
      }
    } else {
      appListUpdateAppStatuses(apps);
    }
  };

  handleSearchChange = ({ target: { name, value } }) => this.handleChange(name, value);

  handleSearchBlur = ({ target: { value } }) => {
    const { list } = this.props;
    const properties = {
      query: value,
      numberOfResults: list.length,
      search_location: LOCATIONS_DISCOVER_PAGE,
    };

    analyticsLeftSearch(properties);
  };

  render() {
    const { openFilters, params } = this.state;
    const {
      t,
      router: { routerMatch },
      list,
      metaData,
      last_page,
      per_page,
      filters,
      loading,
      loadingFilters,
      partner,
      partnerLoading,
      isIframe,
    } = this.props;
    const isPartner = !!partner.slug;

    const loadingContent = loading || loadingFilters;

    const skeletons = new Array(per_page).fill('');
    const certainGenresPath = routerMatch({ path: ROUTE_DISCOVER_GENRE }) && !!metaData.length;

    const seoData = {
      canonicalUrl: `${siteUrl}${ROUTE_DISCOVER_SEARCH}`,
      ogUrl: isPartner
        ? `${siteUrl}${getLink(ROUTE_PARTNER_DISCOVER_SEARCH, { partner: partner.slug })}`
        : `${siteUrl}${ROUTE_DISCOVER_SEARCH}`,
      title:
        partner?.meta?.title || certainGenresPath
          ? metaData[0]?.meta_title
          : 'Discover Streaming TV | MyBundle',
      description:
        partner?.meta?.description || certainGenresPath
          ? metaData[0]?.meta_description
          : 'Browse or use filters above to discover your next favorite Streaming Service.',
      image: partner?.meta?.image || certainGenresPath ? metaData[0]?.og_image_url : defaultOGImg,
      itemList: {
        content_type: 'app',
        data: list.length ? list.slice(0, 4) : [],
      },
    };

    return (
      <Layout>
        {isPartner && partner.meta && !partnerLoading ? (
          <Helmet
            onChangeClientState={analyticsPageLoaded}
            script={[getOrgSchema('wepPage', seoData)]}
            titleTemplate="%s | MyBundle"
          >
            {partner.meta.title && <title>{partner.meta.title}</title>}
            {partner.meta.title && <meta property="og:title" content={partner.meta.title} />}
            <meta property="og:site_name" content="MyBundle" />
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            {partner.meta.description && (
              <meta property="og:description" content={partner.meta.description} />
            )}
            {partner.meta.description && (
              <meta name="description" content={partner.meta.description} />
            )}
            <meta property="og:type" content="website" />
            <meta property="fb:app_id" content="608514030003288" />
            <meta property="og:image" content={seoData.image} />
            <meta name="robots" content="index, follow, max-image-preview:large" />
            <meta
              name="twitter:title"
              content={partner.meta.title || 'MyBundle - Personalized Streaming TV Advice'}
            />
            {partner.meta.description && (
              <meta name="twitter:description" content={partner.meta.description} />
            )}
            <meta name="twitter:image" content={seoData.image} />
            <meta name="twitter:site" content="@mybundletv" />
            <meta name="twitter:creator" content="@mybundletv" />
            <meta name="twitter:card" content="summary" />
          </Helmet>
        ) : (
          <Helmet
            onChangeClientState={analyticsPageLoaded}
            script={[getOrgSchema('wepPage', seoData)]}
            titleTemplate="%s | MyBundle"
          >
            <title>{seoData.title}</title>
            <meta property="og:title" content={seoData.title} />
            <meta property="og:site_name" content="MyBundle" />
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            <meta property="og:description" content={seoData.description} />
            <meta name="description" content={seoData.description} />
            <meta property="og:type" content="website" />
            <meta property="fb:app_id" content="608514030003288" />
            <meta property="og:image" content={seoData.image} />
            <meta name="robots" content="index, follow, max-image-preview:large" />
            <meta name="twitter:title" content={seoData.title} />
            <meta name="twitter:description" content={seoData.description} />
            <meta name="twitter:image" content={seoData.image} />
            <meta name="twitter:site" content="@mybundletv" />
            <meta name="twitter:creator" content="@mybundletv" />
            <meta name="twitter:card" content="summary" />
          </Helmet>
        )}
        <Wrapper
          className={clsx(
            'streaming-marketplace-discover',
            isIframe && 'streaming-marketplace-discover--iframe',
          )}
        >
          <div className="streaming-marketplace-discover__sidebar">
            <Paper
              className="streaming-marketplace-discover__sidebar-paper"
              square
              variant="outlined"
            >
              {filters && (
                <Filters
                  openFilters={openFilters}
                  toggleFilters={this.toggleFilters}
                  selected={params}
                  options={{ ...filters, types }}
                  handleChange={this.handleChange}
                  handleReset={this.handleReset}
                  loadingFilters={loadingFilters}
                />
              )}
            </Paper>
          </div>
          <div className="streaming-marketplace-discover__content">
            <ContentHero
              className="streaming-marketplace-discover__content-hero"
              title={t('streamingMarketplaceDiscover.title')}
              leftBg={false}
              rightBg
            >
              <Grid
                className="streaming-marketplace-discover__filters filters"
                container
                spacing={2}
                wrap="nowrap"
              >
                <Grid item xs>
                  <TextField
                    className="filters__search"
                    variant="filled"
                    name="search"
                    value={params.search}
                    onChange={this.handleSearchChange}
                    onBlur={this.handleSearchBlur}
                    placeholder={t('streamingMarketplaceDiscover.filters.searchPlaceholder')}
                    disabled={loadingFilters}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment className="filters__search__icon">
                          <i className="icon-search" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Hidden mdUp>
                  <Grid item>
                    <Button
                      className="filters__filters-button"
                      variant="contained"
                      color="primary"
                      onClick={this.toggleFilters}
                      disabled={!filters}
                    >
                      <i className="icon-slider" />
                    </Button>
                  </Grid>
                </Hidden>
              </Grid>
            </ContentHero>

            <div className="streaming-marketplace-discover__container section section--grey">
              <div className="page-content">
                <Grid container spacing={3}>
                  {!(loadingContent && params.page === 1) &&
                    list.map((item, idx) => {
                      return (
                        <Grid item xs={6} sm={4} lg={3} key={item.id}>
                          <AppCard
                            data={item}
                            onUpdateStatus={this.handleUpdateAppStatus}
                            onRemoveStatus={this.handleUpdateAppStatus}
                            location={`Slot ${idx + 1}`}
                          />
                        </Grid>
                      );
                    })}
                  {loadingContent &&
                    skeletons.map((item, idx) => {
                      return (
                        <Grid item xs={6} sm={4} lg={3} key={idx}>
                          <AppCardSkeleton type="new-card" />
                        </Grid>
                      );
                    })}
                  {!loadingContent && !list.length && (
                    <Grid item xs={12}>
                      <NoResults search={params.search} handleClick={this.handleReset} />
                    </Grid>
                  )}
                </Grid>
                {last_page > 1 && last_page !== params.page && (
                  <div className="streaming-marketplace-discover__container-controls">
                    <Button
                      color="primary"
                      size="large"
                      onClick={this.handleLoadMore}
                      disabled={loadingContent}
                      endIcon={loadingContent && <CircularProgress color="inherit" size={16} />}
                    >
                      {t('buttons.loadMore')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Wrapper>
      </Layout>
    );
  }
}

StreamingMarketplaceDiscover.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  count_content: PropTypes.object,
  user: PropTypes.object,
  isAuth: PropTypes.bool.isRequired,
  list: PropTypes.array.isRequired,
  metaData: PropTypes.array,
  last_page: PropTypes.number.isRequired,
  current_page: PropTypes.number.isRequired,
  per_page: PropTypes.number.isRequired,
  filters: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  loadingFilters: PropTypes.bool.isRequired,
  getDiscoverList: PropTypes.func.isRequired,
  getDiscoverFilters: PropTypes.func.isRequired,
  addToWatchList: PropTypes.func.isRequired,
  removeFromWatchList: PropTypes.func.isRequired,
  onboardingSetState: PropTypes.func.isRequired,
  partner: PropTypes.object.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
  isIframe: PropTypes.bool.isRequired,
  partnerSlug: PropTypes.string.isRequired,
  cleanDiscoverList: PropTypes.func.isRequired,
  appListUpdateAppStatuses: PropTypes.func.isRequired,
};

const mapStateToProps = ({ auth, discover, partner, app }) => ({
  count_content: (auth.user && auth.user.content_count) || {},
  user: auth.user,
  isAuth: auth.isAuth,
  list: discover.list.data,
  metaData: discover.list.metaData,
  last_page: discover.list.meta.last_page,
  current_page: discover.list.meta.current_page,
  per_page: discover.list.meta.per_page,
  filters: discover.filters,
  loading: discover.loadings.list,
  loadingFilters: discover.loadings.filters,
  partner: partner.data,
  partnerLoading: partner.loadings.partner,
  isIframe: app.isIframe,
  partnerSlug: app.partner,
});

const mapDispatchToProps = dispatch => {
  return {
    getDiscoverList: params => {
      dispatch(DiscoverActions.getDiscoverList.action({ params }));
    },

    getDiscoverFilters: () => {
      dispatch(DiscoverActions.getDiscoverFilters.action());
    },

    addToWatchList: (content_id, content_type, content_status, callback) => {
      dispatch(
        WatchListActions.addToWatchList.action({
          content_id,
          content_type,
          content_status,
          callback,
        }),
      );
    },
    removeFromWatchList: (content_id, content_type, callback) => {
      dispatch(WatchListActions.removeFromWatchList.action({ content_id, content_type, callback }));
    },
    onboardingSetState: state => dispatch(OnboardingActions.onboardingSetState.action({ state })),
    cleanDiscoverList: () => dispatch(DiscoverActions.cleanDiscoverList.action()),
    appListUpdateAppStatuses: apps => {
      dispatch(AppDetailsActions.appListUpdateAppStatuses.action({ apps }));
    },
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(),
  withRouter,
)(StreamingMarketplaceDiscover);
